import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Order } from "../types";
import { fetchOrders } from "../services/orderService";
import { addToCart } from "../services/cartService";
import Loader from "../components/loader/Loader";

// Функция перевода статуса в читабельный вид
const getStatusLabel = (status: string) => {
  switch (status) {
    case "pending":
      return "Оформление заказа";
    case "processing":
      return "Обработка";
    case "shipped":
      return "Отправлен";
    case "delivered":
      return "Выдан";
    case "canceled":
      return "Отменен";
    case "returned":
      return "Возврат";
    default:
      return status;
  }
};

const OrderDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [orderDetails, setOrderDetails] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setIsLoading(true);
        // Загружаем все заказы
        const orders = await fetchOrders();
        // Находим нужный заказ
        const order = orders.find((o) => o.id === Number(id)) || null;
        setOrderDetails(order);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Неизвестная ошибка";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrderDetails();
  }, [id]);

  // Форматируем дату, если есть
  const orderDate = orderDetails?.created_at
    ? new Date(orderDetails.created_at).toLocaleString("ru-RU")
    : null;

  // Повторить заказ: добавляем все товары в корзину и уводим на /cart
  const handleRepeatOrder = async () => {
    if (!orderDetails) return;
    try {
      orderDetails.items &&
        orderDetails.items.forEach(async (item) => {
          await addToCart(item.product_id, item.quantity);
        });
      // for (const item of orderDetails.items) {
      //   await addToCart(item.product_id, item.quantity);
      // }
      navigate("/cart");
    } catch (err) {
      console.error("Ошибка при повторном заказе:", err);
    }
  };

  if (isLoading) return <Loader />;

  if (error) {
    return <div className="text-center text-red-500">{error}</div>;
  }

  if (!orderDetails) {
    return <div className="text-center">Заказ не найден</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Детали заказа №{orderDetails.id}</h2>

      {/* Основная информация о заказе */}
      <div className="mb-4">
        <p>
          <strong>Статус:</strong> {getStatusLabel(orderDetails.status)}
        </p>
        {orderDate && (
          <p>
            <strong>Дата заказа:</strong> {orderDate}
          </p>
        )}
        <p>
          <strong>Сумма заказа:</strong> {orderDetails.total_amount} ₸
        </p>

        {/* Способ доставки (delivery/pickup) */}
        <p>
          <strong>Способ доставки:</strong>{" "}
          {orderDetails.delivery_type === "delivery" ? "Доставка" : "Самовывоз"}
        </p>

        {/* Адрес, если доставка */}
        {orderDetails.delivery_type === "delivery" && orderDetails.address && (
          <p>
            <strong>Адрес доставки:</strong>{" "}
            {orderDetails.address.street},{" "}
            дом {orderDetails.address.house}
            {orderDetails.address.apartment &&
              `, кв. ${orderDetails.address.apartment}`}
          </p>
        )}

        {/* Комментарий, если есть */}
        {orderDetails.comment && (
          <p>
            <strong>Комментарий:</strong> {orderDetails.comment}
          </p>
        )}
      </div>

      {/* Список товаров */}
      <h3 className="text-xl font-bold mb-2">Товары в заказе:</h3>
      <div className="border p-4 rounded-lg mb-6">
        {orderDetails.items && orderDetails.items.length > 0 ? (
          orderDetails.items.map((item) => {
            const productName = item.product?.name || "Товар без названия";
            const wasPrice = item.price; // по какой цене купили
            const currentPrice = item.product?.price ?? wasPrice; // текущая цена, если есть
            const priceChanged = wasPrice !== currentPrice;

            return (
              <div
                key={item.product_id}
                className="flex justify-between items-center border-b pb-2 mb-2"
                onClick={() => navigate(`/product/${item.product_id}`)}
              >
                <div>
                  <h3 className="font-bold">{productName}</h3>
                  <p>{item.quantity} шт.</p>
                  {priceChanged ? (
                    <div>
                      <span className="line-through text-gray-400 mr-2">
                        {wasPrice} ₸
                      </span>
                      <span className="text-red-600">
                        Текущая цена: {currentPrice} ₸
                      </span>
                    </div>
                  ) : (
                    <p>Цена: {wasPrice} ₸</p>
                  )}
                </div>
                {/* Картинка товара (если есть) */}
                {item.product?.images?.[0] && (
                  <img
                    src={item.product.images[0]}
                    alt={productName}
                    className="w-16 h-16 object-cover"
                  />
                )}
              </div>
            );
          })
        ) : (
          <p>Нет товаров в заказе</p>
        )}
        <p className="font-bold mt-2">
          Итого: {orderDetails.total_amount} ₸
        </p>
      </div>

      {/* Кнопки управления */}
      <div className="flex space-x-2">
        <button
          className="px-4 py-2 border  rounded hover:bg-pink-300"
          onClick={handleRepeatOrder}
        >
          Повторить заказ
        </button>
        <button
          className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
          onClick={() => navigate(-1)}
        >
          Назад
        </button>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
